"use client";

import { hostToolWireName } from "@magnus/contract/host-tools";
import { type ComponentType, type LazyExoticComponent, useMemo } from "react";
import type { ToolCategory, ToolKind } from "../index";
import { useMagnusClient } from "./provider";

export type ToolView = ComponentType<{ invocation: any }>;
export type LazyToolView = LazyExoticComponent<ToolView>;

export interface ToolUIMeta {
  category: ToolCategory;
  kind: ToolKind;
  view: ToolView | LazyToolView;
}

/** Map of tool name -> presentation metadata. App-owned per ADR-039. */
export type ToolUIRegistry = Record<string, ToolUIMeta>;

/**
 * `tool-bash` -> `bash`. Internal helper used when resolving a part to its
 * registered view; the transcript exports a name-typed variant from
 * `chat/transcript`.
 */
export function partTypeToToolName(type: string): string {
  return type.startsWith("tool-") ? type.slice("tool-".length) : type;
}

/**
 * Build a registry from `defineClientTool` tools that declared a `display`
 * with a `view`. Apps merge this with their own static registry of views
 * for server-side tools.
 */
export function useToolRegistry(extra?: ToolUIRegistry): ToolUIRegistry {
  const { surface, tools } = useMagnusClient();
  return useMemo(() => {
    const registry: ToolUIRegistry = {};
    for (const tool of tools) {
      const display = tool.display;
      if (display?.view) {
        const meta = {
          kind: display.kind,
          category: display.category,
          view: display.view as ToolView | LazyToolView,
        };
        registry[tool.name] = meta;
        // Host-declared tool parts stream back under the surface-namespaced
        // wire name (`{surface}__{name}`, ADR-096); alias it so the view
        // still resolves.
        if (surface) {
          registry[hostToolWireName(surface, tool.name)] = meta;
        }
      }
    }
    return { ...registry, ...extra };
  }, [surface, tools, extra]);
}
